/**
 * FlowDrivenBuilder - Dynamic Visual Builder driven by backend flow configuration
 * 
 * This component renders product configuration steps based on flow definitions
 * stored in the product_config_flows collection. It supports:
 * - Dynamic step rendering from backend config
 * - Visual Builder assets integration (images, labels)
 * - Flow versioning for order locking
 * - Single/multi-select modes per step
 * - Validation rules
 * - Multi-currency display (EUR base, converted to customer currency)
 * 
 * Hardcoded elements (by design):
 * - Quantity selector (global control)
 * - Order summary/footer (layout component)
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { 
  Check, ChevronLeft, ChevronRight, Loader2, AlertCircle,
  Disc, Package, Palette, Layers, Plus, Minus, X, ChevronDown, Search
} from 'lucide-react';
import { Button } from './ui/button';
import { useCurrency, formatCurrencyAmount, convertFromEur, roundCurrencyAmount } from '../hooks/useCurrency';
import { OptionCard as MarketingOptionCard, usePublicUI } from '@owntune/ui';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../contexts/TranslationContext';

import { API_BASE } from '../lib/apiBase';
const API_URL = API_BASE;
const getToken = () => localStorage.getItem('token');

// Normalises a sales SKU string so trivial config typos ("BD_Master" vs
// "BD Master") don't silently drop setup fees / per-unit addons from the
// customer price. Both sides of every SKU lookup go through this so the
// map is indexed under BOTH the raw AND the normalised key.
const normalizeSkuKey = (s) => (s == null ? '' : String(s).trim().replace(/[_\s]+/g, ' ').toLowerCase());

// Quantity presets — fallback defaults used only when the admin has
// not saved a custom set for this product family. Live presets are
// fetched by the top-level builder and passed into QuantitySelector.
const DEFAULT_QUANTITY_PRESETS = [250, 300, 500, 1000, 2000];
const DEFAULT_MIN_QTY = 50;
const DEFAULT_MAX_QTY = 10000;

// Icon mapping for step categories
const STEP_ICONS = {
  format: Disc,
  record_weight: Disc,
  record_color: Palette,
  vinyl_label: Disc,  // Label is part of the record
  inner_sleeve: Layers,
  cover: Package,
  cover_finish: Layers,
  extras: Plus,
  default: Layers
};

/**
 * ListBackedSelector - Searchable selector that fetches items from an attribute list
 */
function ListBackedSelector({
  field,
  value,
  onChange,
  hasError = false
}) {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Fetch items from the attribute list
  useEffect(() => {
    if (!field.source_list_id) return;
    
    const fetchItems = async () => {
      setLoading(true);
      try {
        const searchParam = search ? `?search=${encodeURIComponent(search)}&limit=50` : '?limit=100';
        const res = await fetch(`${API_URL}/api/product-config/attribute-lists/${field.source_list_id}/items${searchParam}`);
        if (res.ok) {
          const data = await res.json();
          setItems(data);
        }
      } catch (err) {
        console.error('Failed to fetch list items:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchItems();
  }, [field.source_list_id, search]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isMulti = field.selection_mode === 'multi';
  const selectedValues = isMulti ? (Array.isArray(value) ? value : []) : (value ? [value] : []);
  const selectedItems = items.filter(i => selectedValues.includes(i.value));
  
  // For display, show selected labels
  const displayText = selectedItems.length > 0 
    ? selectedItems.map(i => i.label).join(', ')
    : field.placeholder || 'Select...';

  const handleSelect = (itemValue) => {
    if (isMulti) {
      const maxSelections = field.max_selections;
      if (selectedValues.includes(itemValue)) {
        onChange(selectedValues.filter(v => v !== itemValue));
      } else if (!maxSelections || selectedValues.length < maxSelections) {
        onChange([...selectedValues, itemValue]);
      }
    } else {
      onChange(itemValue);
      setIsOpen(false);
    }
  };

  const handleRemove = (itemValue) => {
    if (isMulti) {
      onChange(selectedValues.filter(v => v !== itemValue));
    } else {
      onChange('');
    }
  };

  return (
    <div ref={dropdownRef} className="relative">
      {/* Display selected values as chips for multi-select */}
      {isMulti && selectedItems.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {selectedItems.map(item => (
            <span 
              key={item.value}
              className="inline-flex items-center gap-1 px-2 py-1 bg-indigo-100 text-indigo-700 rounded-full text-sm"
            >
              {item.label}
              <button
                type="button"
                onClick={() => handleRemove(item.value)}
                className="hover:bg-indigo-200 rounded-full p-0.5"
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}
      
      {/* Dropdown trigger */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full px-3 py-2 text-left border rounded-lg text-sm flex items-center justify-between ${
          hasError ? 'border-red-500 bg-red-50' : isOpen ? 'border-indigo-500 ring-2 ring-indigo-200' : 'border-slate-300'
        } ${selectedItems.length === 0 ? 'text-slate-500' : 'text-slate-900'}`}
        data-testid={`field-${field.field_id}-trigger`}
      >
        <span className="truncate">{isMulti && selectedItems.length > 0 ? t('flow.n_selected', '{n} selected', { n: selectedItems.length }) : displayText}</span>
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {/* Dropdown menu */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-64 overflow-hidden">
          {/* Search input */}
          <div className="p-2 border-b border-slate-100">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-8 pr-3 py-1.5 text-sm border border-slate-200 rounded focus:ring-1 focus:ring-indigo-500"
                placeholder={t('common.search_placeholder', 'Search...')}
                onClick={(e) => e.stopPropagation()}
                data-testid={`field-${field.field_id}-search`}
              />
            </div>
          </div>
          
          {/* Options list */}
          <div className="max-h-48 overflow-y-auto">
            {loading ? (
              <div className="p-3 text-center text-slate-500 text-sm">{t('common.loading_dots', 'Loading...')}</div>
            ) : items.length === 0 ? (
              <div className="p-3 text-center text-slate-500 text-sm">
                {search ? t('common.no_results', 'No results found') : t('common.no_items', 'No items available')}
              </div>
            ) : (
              items.map(item => {
                const isSelected = selectedValues.includes(item.value);
                return (
                  <button
                    key={item.value}
                    type="button"
                    onClick={() => handleSelect(item.value)}
                    className={`w-full px-3 py-2 text-left text-sm flex items-center justify-between hover:bg-slate-50 ${
                      isSelected ? 'bg-indigo-50' : ''
                    }`}
                    data-testid={`field-${field.field_id}-option-${item.value}`}
                  >
                    <span>{item.label}</span>
                    {isSelected && <Check className="w-4 h-4 text-indigo-600" />}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
      
      {/* Help text */}
      {field.help_text && (
        <p className="text-xs text-slate-500 mt-1">{field.help_text}</p>
      )}
    </div>
  );
}

/**
 * AdditionalFieldsPanel - Renders metadata fields for a selected option
 * Supports both inline options and list-backed selectors
 * These fields don't affect pricing - just collect extra info
 */
function AdditionalFieldsPanel({ 
  fields, 
  values, 
  onChange,
  optionLabel,
  stepId,
  optionId,
  errors = {}
}) {
  if (!fields || fields.length === 0) return null;

  const handleFieldChange = (fieldId, value) => {
    onChange({ ...values, [fieldId]: value });
  };

  const getErrorKey = (fieldId) => `${stepId}-${optionId}-${fieldId}`;

  return (
    <div className="mt-4 p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-4">
      <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">
        {optionLabel} Details
      </p>
      
      {fields.map(field => {
        const errorKey = getErrorKey(field.field_id);
        const hasError = !!errors[errorKey];
        
        return (
          <div key={field.field_id} className="space-y-1.5">
            <label className="block text-sm font-medium text-slate-700">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            
            {/* List-backed selector (new) */}
            {field.source_list_id && (
              <ListBackedSelector
                field={field}
                value={values?.[field.field_id]}
                onChange={(v) => handleFieldChange(field.field_id, v)}
                hasError={hasError}
              />
            )}
            
            {/* Inline text field */}
            {!field.source_list_id && field.field_type === 'text' && (
              <input
                type="text"
                value={values?.[field.field_id] || ''}
                onChange={(e) => handleFieldChange(field.field_id, e.target.value)}
                placeholder={field.placeholder || ''}
                className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                  hasError ? 'border-red-500 bg-red-50' : 'border-slate-300'
                }`}
                data-testid={`field-${field.field_id}`}
              />
            )}
            
            {/* Inline number field */}
            {!field.source_list_id && field.field_type === 'number' && (
              <input
                type="number"
                value={values?.[field.field_id] || ''}
                onChange={(e) => handleFieldChange(field.field_id, e.target.value)}
                placeholder={field.placeholder || ''}
                className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                  hasError ? 'border-red-500 bg-red-50' : 'border-slate-300'
                }`}
                data-testid={`field-${field.field_id}`}
              />
            )}
            
            {/* Inline date field */}
            {!field.source_list_id && field.field_type === 'date' && (
              <input
                type="date"
                value={values?.[field.field_id] || ''}
                onChange={(e) => handleFieldChange(field.field_id, e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                  hasError ? 'border-red-500 bg-red-50' : 'border-slate-300'
                }`}
                data-testid={`field-${field.field_id}`}
              />
            )}
            
            {/* Inline dropdown (legacy - no source_list_id) */}
            {!field.source_list_id && field.field_type === 'dropdown' && field.options && (
              <select
                value={values?.[field.field_id] || ''}
                onChange={(e) => handleFieldChange(field.field_id, e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white ${
                  hasError ? 'border-red-500 bg-red-50' : 'border-slate-300'
                }`}
                data-testid={`field-${field.field_id}`}
              >
                <option value="">{field.placeholder || 'Select an option...'}</option>
                {field.options?.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            )}
            
            {/* Inline multi-select (legacy - no source_list_id) */}
            {!field.source_list_id && field.field_type === 'multi-select' && field.options && (
              <div className={`flex flex-wrap gap-2 ${hasError ? 'p-2 border border-red-500 rounded-lg bg-red-50' : ''}`}>
                {field.options?.map(opt => {
                  const currentValues = values?.[field.field_id] || [];
                  const isSelected = currentValues.includes(opt.value);
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => {
                        const newValues = isSelected
                          ? currentValues.filter(v => v !== opt.value)
                          : [...currentValues, opt.value];
                        handleFieldChange(field.field_id, newValues);
                      }}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                        isSelected
                          ? 'bg-indigo-100 text-indigo-700 border-2 border-indigo-300'
                          : 'bg-white text-slate-600 border border-slate-300 hover:border-slate-400'
                      }`}
                      data-testid={`field-${field.field_id}-${opt.value}`}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            )}
            
            {/* Error message */}
            {hasError && (
              <p className="text-xs text-red-600 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                {errors[errorKey]}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}

/**
 * VisualOptionCard - Premium product selection card
 * Large product images with subtle pricing
 */
function VisualOptionCard({ 
  asset, 
  isSelected, 
  onClick, 
  selectionMode = 'single',
  size = 'default',
  priceInfo = null, // { unitPrice, totalAddon, type }
  customerCurrency = 'EUR',
  rates = null,
  isMandatory = false,
  // When false, suppress the price chip. Calc/selection untouched.
  showPrices = true
}) {
  const { active: publicUI } = usePublicUI();
  const hasPrice = showPrices && priceInfo && (priceInfo.unitPrice > 0 || priceInfo.totalAddon > 0);
  const isAmortized = priceInfo?.type === 'one_time_amortized';

  // For amortized options the chip must show the FLAT total (that's the
  // only number that lands on the invoice) — not a "/unit" price which
  // would misleadingly imply the customer is being charged per copy.
  const chipAmountEur = isAmortized ? priceInfo?.totalAddon : priceInfo?.unitPrice;
  const displayUnitPrice = hasPrice && rates && chipAmountEur != null
    ? convertFromEur(chipAmountEur, customerCurrency, rates)
    : chipAmountEur;

  // Anonymous marketing/campaign flow — delegate to the shared
  // @owntune/ui OptionCard so we render the same UI as the marketing
  // site (dark card, amber accent, --shadow-gold). Business logic
  // above (price resolution, mandatory guards) is unchanged.
  if (publicUI) {
    const priceLabel = hasPrice
      ? `+${formatCurrencyAmount(displayUnitPrice, customerCurrency, { decimals: 2 })}${isAmortized ? ' total' : '/unit'}`
      : null;
    return (
      <MarketingOptionCard
        label={asset.label}
        description={asset.short_description}
        imageUrl={asset.image_url}
        isSelected={isSelected}
        isMandatory={isMandatory}
        priceLabel={priceLabel}
        onClick={onClick}
        fallbackIcon={<Disc className="w-14 h-14" />}
        dataTestid={`option-${asset.option_id}`}
      />
    );
  }

  return (
    <button
      onClick={onClick}
      disabled={isMandatory && isSelected}
      className={`
        group relative flex flex-col h-auto rounded-2xl transition-all duration-200
        ${isSelected 
          ? 'bg-indigo-50/80 ring-2 ring-indigo-400 shadow-xl shadow-indigo-100/60' 
          : 'bg-white border border-slate-200/80 hover:border-slate-300 hover:shadow-lg hover:bg-slate-50/30'
        }
        ${isMandatory && isSelected ? 'cursor-default' : ''}
      `}
      data-testid={`option-${asset.option_id}`}
    >
      {/* Mandatory badge */}
      {isMandatory && (
        <div className="absolute top-3 left-3 z-10 px-2 py-0.5 bg-slate-600 text-white text-xs font-medium rounded-full shadow-sm">
          Mandatory
        </div>
      )}
      
      {/* Selection indicator */}
      {isSelected && (
        <div className="absolute top-3 right-3 z-10 w-7 h-7 bg-indigo-500 rounded-full flex items-center justify-center shadow-lg">
          <Check className="w-4 h-4 text-white" strokeWidth={3} />
        </div>
      )}
      
      {/* Image Area — flexible height, shrinks at narrow widths */}
      <div className={`
        relative w-full flex items-center justify-center rounded-t-2xl overflow-hidden
        max-h-[120px] sm:max-h-[160px] lg:max-h-[180px]
        ${isSelected ? 'bg-gradient-to-b from-indigo-100/50 to-transparent' : 'bg-gradient-to-b from-slate-50 to-transparent'}
      `}>
        {asset.image_url ? (
          <img 
            src={asset.image_url} 
            alt={asset.label}
            className="w-full h-full object-contain p-3 sm:p-4 max-h-[120px] sm:max-h-[160px] lg:max-h-[180px]"
          />
        ) : (
          <Disc className={`w-14 h-14 sm:w-16 sm:h-16 my-4 ${isSelected ? 'text-indigo-300' : 'text-slate-200'}`} />
        )}
      </div>
      
      {/* Content Area — flex-1 so it fills remaining space, price pushed to bottom */}
      <div className="flex flex-col flex-1 p-3 sm:p-4 pt-2 text-center">
        {/* Label — always visible */}
        <h3 className={`
          font-semibold text-sm sm:text-base
          ${isSelected ? 'text-indigo-900' : 'text-slate-800'}
        `}>
          {asset.label}
        </h3>
        
        {/* Short Description — can truncate */}
        {asset.short_description && (
          <p className={`
            text-xs sm:text-sm mt-1 line-clamp-2
            ${isSelected ? 'text-indigo-600/80' : 'text-slate-500'}
          `}>
            {asset.short_description}
          </p>
        )}
        
        {/* Price — always visible, pushed to bottom */}
        {hasPrice && (
          <p className={`
            text-xs mt-auto pt-2 font-medium
            ${isSelected ? 'text-indigo-500' : 'text-slate-400'}
          `}>
            +{formatCurrencyAmount(displayUnitPrice, customerCurrency, { decimals: 2 })}
            {isAmortized ? ' total' : '/unit'}
          </p>
        )}
      </div>
    </button>
  );
}

/**
 * ToggleOptionCard - For multi-select "extras" style options
 */
function ToggleOptionCard({ asset, isSelected, onClick, isMandatory = false }) {
  return (
    <button
      onClick={onClick}
      disabled={isMandatory && isSelected}
      className={`
        flex items-center gap-4 p-4 rounded-xl border-2 transition-all w-full text-left
        ${isSelected 
          ? 'border-indigo-500 bg-indigo-50' 
          : 'border-border bg-card hover:border-slate-300'
        }
        ${isMandatory && isSelected ? 'cursor-default' : ''}
      `}
      data-testid={`toggle-${asset.option_id}`}
    >
      {/* Checkbox indicator */}
      <div className={`
        w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0
        ${isSelected ? 'bg-indigo-500 border-indigo-500' : 'border-slate-300'}
      `}>
        {isSelected && <Check className="w-3 h-3 text-white" />}
      </div>
      
      {/* Image */}
      {asset.image_url && (
        <img 
          src={asset.image_url} 
          alt={asset.label}
          className="w-12 h-12 object-contain"
        />
      )}
      
      {/* Content */}
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <span className={`font-medium ${isSelected ? 'text-indigo-700' : 'text-slate-900'}`}>
            {asset.label}
          </span>
          {isMandatory && (
            <span className="text-xs px-1.5 py-0.5 bg-slate-600 text-white rounded font-medium">
              Mandatory
            </span>
          )}
        </div>
        {asset.description && (
          <span className="text-sm text-slate-500 block mt-0.5">
            {asset.description}
          </span>
        )}
      </div>
    </button>
  );
}

/**
 * QuantitySelector - Customer-facing quantity control. Presets, min,
 * max, and whether the "Custom" option is available are all injected
 * from the parent builder (which fetches them from the admin-
 * configurable /api/visual-builder/quantity-presets endpoint). Falls
 * back to safe defaults if none are provided.
 */
function QuantitySelector({
  quantity,
  setQuantity,
  format,
  presets = DEFAULT_QUANTITY_PRESETS,
  minQty = DEFAULT_MIN_QTY,
  maxQty = DEFAULT_MAX_QTY,
  customEnabled = true,
}) {
  const [customMode, setCustomMode] = useState(false);
  const { active: publicUI } = usePublicUI();

  // Selected-preset style: on anonymous marketing flows we reuse the
  // exact Sign In page CTA (`.owntune-cta`) so the amber gradient +
  // hover/active/focus states are literally the same component. On
  // authenticated admin/customer pages the legacy indigo pill remains.
  const selectedCls = publicUI
    ? 'owntune-cta btn-active px-4 py-2 rounded-lg'
    : 'bg-indigo-600 text-white shadow-premium-sm px-4 py-2 rounded-lg font-medium transition-all';
  const unselectedCls = 'bg-secondary text-slate-700 hover:bg-slate-200 px-4 py-2 rounded-lg font-medium transition-all';

  const handlePresetClick = (preset) => {
    setQuantity(preset);
    setCustomMode(false);
  };

  const handleCustomChange = (delta) => {
    const newQty = Math.max(minQty, Math.min(maxQty, quantity + delta));
    setQuantity(newQty);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        {presets.map(preset => (
          <button
            key={preset}
            onClick={() => handlePresetClick(preset)}
            className={quantity === preset && !customMode ? selectedCls : unselectedCls}
            data-testid={`qty-${preset}`}
          >
            {preset.toLocaleString()}
          </button>
        ))}
        {customEnabled && (
          <button
            onClick={() => setCustomMode(true)}
            className={customMode ? selectedCls : unselectedCls}
            data-testid="qty-custom-btn"
          >
            Custom
          </button>
        )}
      </div>

      {customEnabled && customMode && (
        <div className="flex items-center gap-3">
          <button
            onClick={() => handleCustomChange(-50)}
            className="w-10 h-10 rounded-lg bg-secondary flex items-center justify-center hover:bg-slate-200"
          >
            <Minus className="w-4 h-4" />
          </button>
          <input
            type="number"
            value={quantity}
            onChange={(e) => setQuantity(Math.max(minQty, parseInt(e.target.value) || minQty))}
            className="w-24 text-center px-3 py-2 border border-border rounded-lg font-medium"
            min={minQty}
            max={maxQty}
            data-testid="qty-custom-input"
          />
          <button
            onClick={() => handleCustomChange(50)}
            className="w-10 h-10 rounded-lg bg-secondary flex items-center justify-center hover:bg-slate-200"
          >
            <Plus className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * FlowStep - Renders a single step from flow config
 */
function FlowStep({ 
  step, 
  assets, 
  selections, 
  onSelect,
  isActive,
  quantity,
  getOptionPriceInfo,
  optionAttributes,
  onAttributeChange,
  attributeErrors = {},
  customerCurrency = 'EUR',
  rates = null,
  // See FlowDrivenBuilder — when "text-hero" and this is the format
  // step, render the compact text-option variant instead of cards.
  recordStepVariant = null,
  // Forwarded from FlowDrivenBuilder — when false, hide the per-option
  // +X/unit price chip (text-hero variant). Calc untouched.
  showPrices = true,
}) {
  // Get assets for this step's category
  // Support both category_id (new) and asset_category (legacy)
  const stepCategoryId = step.category_id || step.asset_category;
  
  const stepAssets = useMemo(() => {
    // Filter assets by category (support both category_id and category for backward compat)
    const categoryAssets = assets.filter(a => {
      const assetCatId = a.category_id || a.category;
      return assetCatId === stepCategoryId && a.is_active !== false;
    });
    
    // If step has specific options defined, filter to only those
    if (step.options && step.options.length > 0) {
      const allowedIds = step.options.map(o => o.option_id);
      // Filter to allowed options, then sort by asset display_order (admin-defined order)
      return categoryAssets
        .filter(a => allowedIds.includes(a.option_id))
        .sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
    }
    
    // Otherwise return all category assets sorted by display_order
    return categoryAssets.sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
  }, [step, assets, stepCategoryId]);

  // Get current selections for this step
  const stepSelections = selections[step.step_id] || [];

  // Get pricing info for options
  const optionPriceMap = useMemo(() => {
    const map = {};
    step.options?.forEach(opt => {
      const priceInfo = getOptionPriceInfo?.(opt, quantity);
      if (priceInfo) {
        map[opt.option_id] = priceInfo;
      }
    });
    return map;
  }, [step.options, quantity, getOptionPriceInfo]);

  // Get additional fields for selected options
  const selectedOptionsWithFields = useMemo(() => {
    return step.options?.filter(opt => 
      stepSelections.includes(opt.option_id) && 
      opt.additional_fields && 
      opt.additional_fields.length > 0
    ) || [];
  }, [step.options, stepSelections]);

  // Check if an option is mandatory
  const isOptionMandatory = useCallback((optionId) => {
    const optionConfig = step.options?.find(o => o.option_id === optionId);
    return optionConfig?.is_mandatory === true;
  }, [step.options]);

  const handleOptionClick = (optionId) => {
    // Prevent deselection of mandatory options
    if (isOptionMandatory(optionId) && stepSelections.includes(optionId)) {
      return; // Don't allow deselection
    }
    
    if (step.selection_mode === 'multi') {
      // Toggle selection
      const newSelections = stepSelections.includes(optionId)
        ? stepSelections.filter(id => id !== optionId)
        : [...stepSelections, optionId];
      onSelect(step.step_id, newSelections);
    } else {
      // Single select - but keep mandatory options
      const mandatoryOptionIds = step.options?.filter(o => o.is_mandatory).map(o => o.option_id) || [];
      const newSelections = [optionId, ...mandatoryOptionIds.filter(id => id !== optionId)];
      onSelect(step.step_id, [...new Set(newSelections)]); // Dedupe
    }
  };

  // Handle attribute change for an option
  const handleAttributeChange = (optionId, newValues) => {
    onAttributeChange(step.step_id, optionId, newValues);
  };

  // Render based on UI type
  if (step.ui_type === 'toggles' && recordStepVariant !== 'text-hero') {
    return (
      <div className="space-y-3">
        {stepAssets.map(asset => {
          const optionConfig = step.options?.find(o => o.option_id === asset.option_id);
          const isSelected = stepSelections.includes(asset.option_id);
          const isMandatory = optionConfig?.is_mandatory === true;
          return (
            <div key={asset.option_id}>
              <ToggleOptionCard
                asset={asset}
                isSelected={isSelected}
                onClick={() => handleOptionClick(asset.option_id)}
                isMandatory={isMandatory}
              />
              {/* Render additional fields if selected and has fields */}
              {isSelected && optionConfig?.additional_fields?.length > 0 && (
                <AdditionalFieldsPanel
                  fields={optionConfig.additional_fields}
                  values={optionAttributes?.[step.step_id]?.[asset.option_id] || {}}
                  onChange={(newValues) => handleAttributeChange(asset.option_id, newValues)}
                  optionLabel={asset.label}
                  stepId={step.step_id}
                  optionId={asset.option_id}
                  errors={attributeErrors}
                />
              )}
            </div>
          );
        })}
      </div>
    );
  }

  // V2 opt-in text-hero variant applied consistently to EVERY step:
  // options rendered as a compact vertical text list on the LEFT beside
  // one large hero image on the RIGHT, reusing the SAME asset images
  // and pricing labels V1 already renders inside its cards. Every
  // caller that omits `recordStepVariant` (i.e. V1 landing pages)
  // continues to see the untouched card grid / toggle stack.
  if (recordStepVariant === 'text-hero' && stepAssets.length > 0) {
    // For single-select steps the hero mirrors the chosen option.
    // For multi-select we prefer the LAST toggled-on option so the
    // hero acknowledges the most recent interaction; falling back to
    // the first available asset before anything is picked.
    const activeAssetId =
      stepSelections[stepSelections.length - 1] || stepAssets[0]?.option_id;
    const activeAsset = stepAssets.find(a => a.option_id === activeAssetId) || stepAssets[0];
    const isMulti = step.selection_mode === 'multi';
    const V2StepIcon = STEP_ICONS[step.asset_category] || STEP_ICONS.default;
    return (
      <div className="space-y-4">
        <div className="grid gap-4 sm:gap-6 md:grid-cols-[minmax(220px,280px)_minmax(0,1fr)] items-start">
          {/* LEFT — step header (moved here from the parent so the
              hero on the right aligns with the top of the "Your
              Order" card) + vertical text-option list. */}
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-indigo-100 rounded-xl">
                <V2StepIcon className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-slate-900 dark:text-foreground leading-tight">
                  {step.label}
                </h2>
                {step.description && (
                  <p className="text-xs text-slate-500 dark:text-muted-foreground">
                    {step.description}
                  </p>
                )}
              </div>
            </div>

            <ul
              className="space-y-2"
              role={isMulti ? 'group' : 'radiogroup'}
              aria-label={step.label || 'Options'}
            >
              {stepAssets.map(asset => {
                const optionConfig = step.options?.find(o => o.option_id === asset.option_id);
                const isMandatory = optionConfig?.is_mandatory === true;
                const isSelected = stepSelections.includes(asset.option_id);
                const priceInfo = optionPriceMap[asset.option_id];
                const hasPrice = showPrices && priceInfo && (priceInfo.unitPrice > 0 || priceInfo.totalAddon > 0);
                const isAmortized = priceInfo?.type === 'one_time_amortized';
                const chipAmountEur = isAmortized ? priceInfo?.totalAddon : priceInfo?.unitPrice;
                const displayUnitPrice = hasPrice && rates && chipAmountEur != null
                  ? convertFromEur(chipAmountEur, customerCurrency, rates)
                  : chipAmountEur;
                const priceLabel = hasPrice
                  ? `+${formatCurrencyAmount(displayUnitPrice, customerCurrency, { decimals: 2 })}${isAmortized ? ' total' : '/unit'}`
                  : null;
                return (
                  <li key={asset.option_id}>
                    <button
                      type="button"
                      role={isMulti ? 'checkbox' : 'radio'}
                      aria-checked={isSelected}
                      disabled={isMandatory && isSelected}
                      onClick={() => handleOptionClick(asset.option_id)}
                      className={`w-full text-left rounded-xl border px-4 py-3 transition-all ${
                        isSelected
                          ? 'border-amber-500 bg-amber-500/[0.08] shadow-[inset_0_0_0_1px_hsl(38_92%_50%)]'
                          : 'border-border bg-transparent hover:border-amber-400 hover:bg-amber-50/40 dark:hover:bg-amber-500/[0.06]'
                      }`}
                      data-testid={`option-${asset.option_id}`}
                    >
                      <span className={`flex items-center gap-2 text-sm font-semibold leading-tight ${isSelected ? 'text-amber-700 dark:text-amber-300' : 'text-foreground'}`}>
                        {asset.label}
                        {isMandatory && (
                          <span className="text-[9px] uppercase tracking-wide px-1.5 py-0.5 bg-slate-600 text-white rounded font-medium">
                              Mandatory
                          </span>
                        )}
                      </span>
                      {(asset.short_description || asset.description) && (
                        <span className="block text-[11px] text-muted-foreground mt-0.5">
                          {asset.short_description || asset.description}
                        </span>
                      )}
                      {priceLabel && (
                        <span className="block text-[11px] font-medium text-amber-600 mt-1">
                          {priceLabel}
                        </span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* RIGHT — one large hero image for the active option. Reuses
              the existing V1 asset image; falls back to a disc icon
              when the option has no artwork attached. The container
              is visually invisible in V2 (no bg, gradient, border,
              radius or shadow) so the transparent hero art sits
              directly on the page background. Dimensions, positioning
              and object-fit sizing are unchanged. */}
          <div className="relative w-full flex items-center justify-center min-h-[280px] sm:min-h-[360px] lg:min-h-[420px]" data-testid={`v2-hero-${step.step_id}`}>
            {activeAsset?.image_url ? (
              <img
                key={activeAsset.option_id}
                src={activeAsset.image_url}
                alt={activeAsset.label}
                className="max-h-full max-w-full w-auto h-auto object-contain p-3"
              />
            ) : (
              <Disc className="w-24 h-24 text-slate-300" />
            )}
          </div>
        </div>

        {/* Additional fields for selected options — same wiring as the
            default renders so per-option inputs (e.g. sleeve text)
            continue to work. */}
        {selectedOptionsWithFields.map(opt => {
          const asset = stepAssets.find(a => a.option_id === opt.option_id);
          return (
            <AdditionalFieldsPanel
              key={opt.option_id}
              fields={opt.additional_fields}
              values={optionAttributes?.[step.step_id]?.[opt.option_id] || {}}
              onChange={(newValues) => handleAttributeChange(opt.option_id, newValues)}
              optionLabel={asset?.label || opt.option_id}
              stepId={step.step_id}
              optionId={opt.option_id}
              errors={attributeErrors}
            />
          );
        })}
      </div>
    );
  }

  // Default: cards layout - larger cards, fewer per row
  // Use xl:grid-cols-4 (not lg) because the sidebar appears at xl and takes 360px,
  // leaving insufficient width for 4 columns at lg breakpoint
  const gridCols = stepAssets.length <= 2 
    ? 'grid-cols-1 sm:grid-cols-2 max-w-2xl mx-auto' 
    : stepAssets.length <= 3
      ? 'grid-cols-1 sm:grid-cols-3'
      : 'grid-cols-2 sm:grid-cols-3 xl:grid-cols-4';

  return (
    <div className="space-y-4">
      <div className={`grid ${gridCols} gap-5`}>
        {stepAssets.map(asset => {
          const optionConfig = step.options?.find(o => o.option_id === asset.option_id);
          const isMandatory = optionConfig?.is_mandatory === true;
          return (
            <VisualOptionCard
              key={asset.option_id}
              asset={asset}
              isSelected={stepSelections.includes(asset.option_id)}
              onClick={() => handleOptionClick(asset.option_id)}
              selectionMode={step.selection_mode}
              priceInfo={optionPriceMap[asset.option_id]}
              customerCurrency={customerCurrency}
              rates={rates}
              isMandatory={isMandatory}
              showPrices={showPrices}
            />
          );
        })}
      </div>
      
      {/* Render additional fields for selected options */}
      {selectedOptionsWithFields.map(opt => {
        const asset = stepAssets.find(a => a.option_id === opt.option_id);
        return (
          <AdditionalFieldsPanel
            key={opt.option_id}
            fields={opt.additional_fields}
            values={optionAttributes?.[step.step_id]?.[opt.option_id] || {}}
            onChange={(newValues) => handleAttributeChange(opt.option_id, newValues)}
            optionLabel={asset?.label || opt.option_id}
            stepId={step.step_id}
            optionId={opt.option_id}
            errors={attributeErrors}
          />
        );
      })}
    </div>
  );
}

/**
 * ImmersivePriceBar - Sticky footer with price and navigation
 * Displays prices in customer's local currency with EUR reference for non-EUR currencies
 */
function ImmersivePriceBar({ 
  specs, 
  priceEstimate, 
  isCalculating,
  currentStep,
  totalSteps,
  onBack, 
  onNext, 
  canContinue,
  isLastStep,
  totalWithAddons,
  unitPriceWithAddons,
  addonTotal,
  hasValidBasePrice,
  // Currency props
  customerCurrency = 'EUR',
  rates = null,
  currencyLoading = false,
  // Authoritative local-currency price from the backend (shared engine
  // with review page / PDF / saved order). When present, prefer these
  // numbers over the client-side `convertFromEur` conversion so the
  // 0.05-rounding and cross-card lookups match the backend byte for byte.
  backendPrice = null
}) {
  const displayFormat = specs.format?.replace(/"/g, '');
  
  // Determine if we have valid pricing to display
  const hasPrice = totalWithAddons !== null && totalWithAddons > 0;
  const hasAddons = addonTotal !== null && addonTotal > 0;
  
  // Prefer the backend's already-converted, 0.05-rounded local total so
  // "500 × 14.95 kr = 7473 kr" (matches PDF) instead of the client-side
  // conversion which would round each unit independently and drift.
  const hasBackendLocal = backendPrice && backendPrice.total_local != null;
  const convertedTotal = hasBackendLocal
    ? backendPrice.total_local
    : (hasPrice && rates ? convertFromEur(totalWithAddons, customerCurrency, rates) : totalWithAddons);
  const convertedUnitPrice = hasBackendLocal
    ? backendPrice.unit_price_local
    : (unitPriceWithAddons !== null && rates 
      ? convertFromEur(unitPriceWithAddons, customerCurrency, rates) 
      : unitPriceWithAddons);
  const convertedAddonTotal = hasAddons && rates ? convertFromEur(addonTotal, customerCurrency, rates) : addonTotal;
  
  // Determine if we should show EUR reference (for non-EUR customers)
  const showEurReference = customerCurrency !== 'EUR' && rates;
  
  // Format price display
  const formatPrice = (amount, currency, decimals = 0) => {
    if (amount === null || amount === undefined) return '—';
    return formatCurrencyAmount(amount, currency, { decimals });
  };
  
  return (
    <div className="fixed bottom-0 left-0 right-0 bg-card/95 backdrop-blur-sm border-t border-border z-40">
      <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
        {/* Left: Back button */}
        <div className="w-24">
          {currentStep > 0 && onBack && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={onBack}
              className="text-slate-600 hover:text-slate-900"
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Back
            </Button>
          )}
        </div>
        
        {/* Center: Price only (config details live in the sidebar) */}
        <div className="flex items-center gap-4">
          {backendPrice && backendPrice.requires_quote ? (
            // Quote-gated configuration — never show a numeric total. The
            // customer must go through the Request-a-Quote path. The main
            // CTA on the review page is swapped to "Request a Quote"
            // (handled by the parent) — here we just signal the state so
            // nothing in the footer leaks a bogus €0 total.
            <div className="text-center" data-testid="footer-quote-required">
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-300">
                Quote Required
              </span>
              <span className="text-xs text-slate-500 block mt-1">
                Request a priced quote at the end of the flow
              </span>
            </div>
          ) : hasPrice ? (
            // Keep the last-known total visible during recalcs; dim slightly
            // instead of replacing with a spinner so there's no flicker.
            <div className={`text-center transition-opacity duration-150 ${(isCalculating || currencyLoading) ? 'opacity-60' : 'opacity-100'}`}>
              <span className="text-2xl font-bold text-slate-900 font-display" data-testid="unit-price">
                {formatPrice(convertedUnitPrice, customerCurrency, 2)}
                <span className="text-sm font-medium text-slate-500 ml-1">/ unit</span>
              </span>
              <span className="text-xs text-slate-500 block mt-0.5" data-testid="total-price">
                {formatPrice(convertedTotal, customerCurrency, 0)} total
              </span>
              {hasAddons && (
                <span className="text-xs text-amber-600 block">
                  incl. +{formatPrice(convertedAddonTotal, customerCurrency, 0)} upgrades
                </span>
              )}
              {showEurReference && (
                <span className="text-xs text-slate-400 block">
                  ≈ €{totalWithAddons.toFixed(0)} EUR
                </span>
              )}
            </div>
          ) : (isCalculating || currencyLoading) ? (
            // First-time mount: subtle skeleton instead of a "Calculating..." flicker.
            <div className="text-center">
              <span className="inline-block h-7 w-24 rounded bg-slate-200/70 animate-pulse align-middle" aria-hidden="true" />
            </div>
          ) : (
            <div className="text-center">
              <span className="text-2xl font-bold text-slate-400">—</span>
            </div>
          )}
        </div>
        
        {/* Right: Continue/Complete CTA */}
        <div className="w-32 flex justify-end">
          {onNext && (
            <Button 
              onClick={onNext}
              disabled={!canContinue}
              className={`${isLastStep ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-indigo-600 hover:bg-indigo-700'} disabled:opacity-50 disabled:cursor-not-allowed`}
              data-testid="flow-continue-btn"
            >
              {isLastStep ? (
                <>
                  <Check className="h-4 w-4 mr-1" />
                  Complete
                </>
              ) : (
                <>
                  Continue
                  <ChevronRight className="h-4 w-4 ml-1" />
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * CompactHeader - Step progress header (no duplicate navigation)
 * Shows step progress only - main navigation is in global header
 */
function CompactHeader({ 
  currentStep, 
  steps, 
  onBack, 
  orderMetadata 
}) {
  const progress = ((currentStep + 1) / steps.length) * 100;
  
  return (
    <div className="bg-white border-b border-slate-200/80">
      {/* Progress bar */}
      <div className="h-1 bg-slate-100">
        <div 
          className="h-full bg-indigo-500 transition-all duration-300 ease-out"
          style={{ width: `${progress}%` }}
        />
      </div>
      
      {/* Step indicator */}
      <div className="max-w-4xl mx-auto px-4 py-3">
        <div className="flex items-center justify-between">
          {/* Step info */}
          <div className="flex items-center gap-3">
            <span className="flex items-center justify-center w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 font-semibold text-sm">
              {currentStep + 1}
            </span>
            <div>
              <p className="text-sm font-medium text-slate-700">
                Step {currentStep + 1} of {steps.length}
              </p>
              <p className="text-xs text-slate-500">
                {steps[currentStep]?.label}
              </p>
            </div>
          </div>
          
          {/* Step navigation pills */}
          <div className="hidden sm:flex items-center gap-1">
            {steps.map((step, idx) => (
              <div
                key={idx}
                className={`
                  flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium transition-all
                  ${idx === currentStep 
                    ? 'bg-indigo-100 text-indigo-700' 
                    : idx < currentStep 
                      ? 'bg-emerald-100 text-emerald-700' 
                      : 'bg-slate-100 text-slate-400'
                  }
                `}
              >
                {idx < currentStep && <Check className="w-3 h-3" />}
                <span className="hidden lg:inline">{step.label}</span>
                <span className="lg:hidden">{idx + 1}</span>
              </div>
            ))}
          </div>
          
          {/* Mobile: Simple dots */}
          <div className="flex sm:hidden items-center gap-1.5">
            {steps.map((_, idx) => (
              <div
                key={idx}
                className={`w-2.5 h-2.5 rounded-full transition-all ${
                  idx === currentStep 
                    ? 'bg-indigo-500 scale-110' 
                    : idx < currentStep 
                      ? 'bg-emerald-400' 
                      : 'bg-slate-200'
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * FlowDrivenBuilder - Main component
 */
export default function FlowDrivenBuilder({
  specs,
  setSpecs,
  getEstimate,
  priceEstimate,
  isCalculating,
  onBack,
  onComplete,
  isImmersiveMode = true,
  orderMetadata = null,
  productFamily = 'vinyl',
  savedFlowConfig = null, // { flowId, flowVersion, selections }
  initialFlowConfig = null, // Optional preloaded flow — skips refetch + loader
  // Public Landing Pages (anonymous) forward a currency handoff from
  // the marketing site's `?currency=` param. Passed through to
  // `useCurrency` so exchange rates + display currency stay consistent
  // with the rest of the anonymous experience.
  currencyOverride = null,
  // OPTIONAL, additive. When set to "text-hero" (and only then) the
  // format/"The Record" step swaps its default product-card grid for
  // a compact text-option list + a single hero using the SAME assets
  // and pricing labels. Every other step, style, spacing, header and
  // sidebar stays byte-identical to V1. Callers that omit this prop
  // (i.e. every existing V1 landing page) see the untouched render.
  recordStepVariant = null,
  // When false (landing page "Show Prices" OFF) every price in the
  // public configurator is hidden: option +X/unit chips, the sidebar
  // "Your Order" price, the mobile summary price, EUR references and
  // the quote-required badge. ALL calculations, fetches, navigation,
  // options, quantities and images are untouched — this ONLY gates
  // the visual price render. Defaults true so authenticated/admin and
  // every existing landing page keep showing prices exactly as before.
  showPrices = true,
}) {
  const { t, language } = useTranslation();
  const { active: publicUI } = usePublicUI();
  // Authenticated caller's active company — used as a fallback for the
  // price-preview `organization_id` when the parent didn't explicitly
  // pass one via ``orderMetadata``. Without this, a fresh Affinity
  // Programme signup (KasKas / DK-Artisten) hitting the configurator
  // sends organization_id=null → backend can't reach the programme's
  // ``default_currency`` → prices show in EUR instead of the
  // programme's currency (DKK for Artisten).
  const { user: authUser } = useAuth();
  // Currency conversion hook — anonymous callers can force a currency
  // via `currencyOverride` so the ?currency= handoff wins over the
  // default EUR fallback.
  const { 
    rates, 
    customerCurrency, 
    loading: currencyLoading 
  } = useCurrency(getToken(), { override: currencyOverride });
  
  // Flow configuration state — when the parent has preloaded the flow we
  // hydrate immediately so there's no flicker between mode-selection and
  // the configurator.
  const [flowConfig, setFlowConfig] = useState(initialFlowConfig);
  const [flowLoading, setFlowLoading] = useState(!initialFlowConfig);
  const [flowError, setFlowError] = useState(null);
  
  // Use ref to track if we've initialized from saved config (survives re-renders)
  const initializedRef = useRef(false);
  
  // Assets state
  const [assets, setAssets] = useState([]);
  
  // Rate card pricing state
  const [rateCardPrices, setRateCardPrices] = useState({}); // { SKU: { tiers: [...] } }
  const [customerDiscount, setCustomerDiscount] = useState(null); // { type, percent, has_sku_overrides }
  
  // Navigation state
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  
  // Mobile summary expanded state
  const [mobileSummaryExpanded, setMobileSummaryExpanded] = useState(false);
  
  // Selections state: { step_id: [option_ids] }
  const [selections, setSelections] = useState({});

  // Admin-configured customer-facing quantity presets for this product
  // family — fetched from /api/visual-builder/quantity-presets/{family}.
  // Falls back silently to the hardcoded defaults on any error so the
  // wizard always renders. NO business-logic side effects — these
  // values only drive the "How many copies?" UI.
  const [qtyPresetsConfig, setQtyPresetsConfig] = useState({
    presets: DEFAULT_QUANTITY_PRESETS,
    min_qty: DEFAULT_MIN_QTY,
    max_qty: DEFAULT_MAX_QTY,
    custom_enabled: true,
  });
  const isVinylFamily = (productFamily || 'vinyl').toLowerCase() === 'vinyl';
  useEffect(() => {
    let cancelled = false;
    const family = (productFamily || 'vinyl').toLowerCase();
    (async () => {
      try {
        const res = await fetch(
          `${API_URL}/api/visual-builder/quantity-presets/${encodeURIComponent(family)}`,
        );
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        if (Array.isArray(data?.presets) && data.presets.length > 0) {
          setQtyPresetsConfig({
            presets: data.presets,
            min_qty: Number(data.min_qty) || DEFAULT_MIN_QTY,
            max_qty: Number(data.max_qty) || DEFAULT_MAX_QTY,
            custom_enabled: data.custom_enabled !== false,
          });
        }
      } catch (_e) {
        // Silent fail — hardcoded defaults above already render.
      }
    })();
    return () => { cancelled = true; };
  }, [productFamily]);

  // Option attributes state: { step_id: { option_id: { field_id: value } } }
  // Stores metadata field values for selected options (no pricing impact)
  const [optionAttributes, setOptionAttributes] = useState({});

  // Authoritative price from the SHARED backend engine — populated via
  // `POST /api/sales/flow-price-preview`. This is the SAME engine that
  // powers the review page, quote PDF, saved-order document and
  // e-conomic sync. The Visual Builder MUST display these numbers
  // rather than duplicate the pricing math client-side; historical
  // divergence (e.g. one_time_amortized being counted twice on Blu-ray
  // because a stale legacy vinyl estimator was added on top of the
  // amortized amount) is prevented by anchoring on this response.
  //
  // Shape: { total, unit_price, currency, total_local, unit_price_local,
  //          currency_local, exchange_rate, requires_quote, quote_gated_skus }
  const [backendPrice, setBackendPrice] = useState(null);

  // Helper to get unit price for a SKU at given quantity
  const getSkuPrice = useCallback((sku, quantity) => {
    const skuData = rateCardPrices[sku] || rateCardPrices[normalizeSkuKey(sku)];
    if (!skuData || !skuData.tiers || skuData.tiers.length === 0) {
      return null;
    }
    
    // Find the applicable tier (highest min_qty that's <= quantity)
    const tiers = [...skuData.tiers].sort((a, b) => b.min_qty - a.min_qty);
    const applicableTier = tiers.find(t => t.min_qty <= quantity) || tiers[tiers.length - 1];
    return applicableTier?.unit_price ?? null;
  }, [rateCardPrices]);

  // Helper to get setup fee for a SKU (only for new orders - Visual Builder creates new orders)
  // SKU-based setup fees with per-SKU applicability
  // Setup fees are looked up at the ORDER quantity (not qty=1)
  const getSkuSetupFee = useCallback((sku, orderQuantity) => {
    const skuData = rateCardPrices[sku] || rateCardPrices[normalizeSkuKey(sku)];
    if (!skuData) return 0;
    
    // Check for SKU-based setup fees
    const setupFeeSkus = skuData.setup_fee_skus || [];
    if (setupFeeSkus.length === 0) return 0;
    
    // Sum up prices from setup fee SKUs that apply to new orders
    // Visual Builder = always new orders
    let totalSetupFee = 0;
    for (const feeEntry of setupFeeSkus) {
      // Handle both old format (string) and new format (object with sku + applies_to)
      let feeSku, appliesTo;
      if (typeof feeEntry === 'string') {
        // Old format - assume applies to new_order
        feeSku = feeEntry;
        appliesTo = ['new_order'];
      } else {
        // New format - { sku: "...", applies_to: [...] }
        feeSku = feeEntry.sku;
        appliesTo = feeEntry.applies_to || ['new_order'];
      }
      
      // Only include if applies to new orders
      if (!appliesTo.includes('new_order')) continue;
      
      const feeSkuData = rateCardPrices[feeSku] || rateCardPrices[normalizeSkuKey(feeSku)];
      if (feeSkuData?.tiers?.length > 0) {
        // Get price at ORDER quantity for setup fees (tier-based pricing)
        const tiers = [...feeSkuData.tiers].sort((a, b) => a.min_qty - b.min_qty);
        // Find the highest tier that the quantity qualifies for
        let matchedTier = tiers[0];
        for (const tier of tiers) {
          if (orderQuantity >= tier.min_qty) {
            matchedTier = tier;
          }
        }
        totalSetupFee += matchedTier?.unit_price || 0;
      }
    }
    
    return totalSetupFee;
  }, [rateCardPrices]);

  // Helper: sum per-unit add-ons attached to a base SKU. Mirrors the
  // backend pricing engine — each linked add-on is billed at
  // `unit_price(qty) × qty`, cross-card lookups are transparent because
  // the backend inlines those SKUs into `rateCardPrices`.
  const getSkuPerUnitAddons = useCallback((sku, orderQuantity) => {
    const skuData = rateCardPrices[sku] || rateCardPrices[normalizeSkuKey(sku)];
    if (!skuData) return 0;
    const addonSkus = skuData.per_unit_addon_skus || [];
    if (addonSkus.length === 0) return 0;
    let total = 0;
    for (const entry of addonSkus) {
      let addonSku, appliesTo;
      if (typeof entry === 'string') {
        addonSku = entry;
        appliesTo = ['new_order'];
      } else {
        addonSku = entry.sku;
        appliesTo = entry.applies_to || ['new_order'];
      }
      if (!appliesTo.includes('new_order')) continue;
      const addonPrice = getSkuPrice(addonSku, orderQuantity);
      if (addonPrice && addonPrice > 0) {
        total += addonPrice * orderQuantity;
      }
    }
    return total;
  }, [rateCardPrices, getSkuPrice]);

  // Get option price info for display (addon badge)
  const getOptionPriceInfo = useCallback((option, quantity) => {
    if (!option.pricing_mapping?.sales_sku) return null;
    if (option.is_included_in_base) return null;

    const pm = option.pricing_mapping;
    const type = pm.type || 'addon';

    // `one_time_amortized` — the SKU price is a FLAT lump sum. The
    // customer-facing chip must show "+X total" (not per-unit) so the
    // number in the chip matches what actually lands on the invoice.
    if (type === 'one_time_amortized') {
      const amount = pm.fixed_amount != null
        ? Number(pm.fixed_amount)
        : getSkuPrice(pm.sales_sku, 1);
      if (!amount || amount <= 0) return null;
      const qty = quantity || 1;
      return {
        unitPrice: amount / qty,   // amortized per-unit (informational)
        totalAddon: amount,        // authoritative flat charge
        type,
      };
    }

    const unitPrice = getSkuPrice(pm.sales_sku, quantity);
    if (unitPrice === null || unitPrice === 0) return null;

    const multiplier = pm.quantity_multiplier || 1;
    const totalAddon = unitPrice * quantity * multiplier;

    return {
      unitPrice,
      totalAddon,
      type,
    };
  }, [getSkuPrice]);

  // Calculate total addon costs from selections up to a given step (includes setup fees for addons)
  // Only includes steps the user has reached (up to and including maxStepIndex)
  // Returns null if no addon pricing is configured, 0 if addons are configured but free
  const calculateAddonTotal = useCallback((currentSelections, quantity, maxStepIndex = Infinity) => {
    if (!flowConfig?.steps) return null;
    
    let addonTotal = 0;
    let hasAnyAddonSku = false;
    
    flowConfig.steps.forEach((step, stepIndex) => {
      // Only include steps up to and including the current step
      if (stepIndex > maxStepIndex) return;
      
      const selectedOptionIds = currentSelections[step.step_id] || [];
      
      selectedOptionIds.forEach(optionId => {
        // Find the option config
        const optionConfig = step.options?.find(o => o.option_id === optionId);
        if (!optionConfig) return;
        
        // Skip if included in base or is a base SKU
        if (optionConfig.is_included_in_base) return;
        
        // Get pricing mapping
        const pm = optionConfig.pricing_mapping;
        if (!pm?.sales_sku) return;
        
        // Skip base SKUs - they're calculated separately
        if (pm.type === 'base') return;

        // One-time amortized — treat like an addon in the running total,
        // but bill the FLAT amount regardless of quantity.
        if (pm.type === 'one_time_amortized') {
            hasAnyAddonSku = true;
            let amount;
            if (pm.fixed_amount != null) {
                amount = Number(pm.fixed_amount);
            } else {
                amount = getSkuPrice(pm.sales_sku, 1);
            }
            if (amount && amount > 0) addonTotal += amount;
            return;
        }

        hasAnyAddonSku = true;
        
        // Get unit price from rate card
        const unitPrice = getSkuPrice(pm.sales_sku, quantity);
        if (unitPrice === null) return; // SKU not in rate card
        
        // Calculate addon cost
        const multiplier = pm.quantity_multiplier || 1;
        addonTotal += unitPrice * quantity * multiplier;
        
        // Add setup fee for addon (one-time fee, based on order quantity)
        const setupFee = getSkuSetupFee(pm.sales_sku, quantity);
        addonTotal += setupFee;
      });
    });
    
    return addonTotal;
  }, [flowConfig, getSkuPrice, getSkuSetupFee]);

  // Calculate base price from selected base SKUs up to a given step (includes setup fees)
  // Only includes steps the user has reached (up to and including maxStepIndex)
  // Returns null if no base pricing is configured
  const calculateBasePrice = useCallback((currentSelections, quantity, maxStepIndex = Infinity) => {
    if (!flowConfig?.steps) return null;
    
    let baseTotal = 0;
    let setupFeeTotal = 0;
    let hasBaseSku = false;
    
    flowConfig.steps.forEach((step, stepIndex) => {
      // Only include steps up to and including the current step
      if (stepIndex > maxStepIndex) return;
      
      const selectedOptionIds = currentSelections[step.step_id] || [];
      
      selectedOptionIds.forEach(optionId => {
        // Find the option config
        const optionConfig = step.options?.find(o => o.option_id === optionId);
        if (!optionConfig) return;
        
        // Get pricing mapping
        const pm = optionConfig.pricing_mapping;
        if (!pm?.sales_sku) return;
        
        // Only process base SKUs
        if (pm.type !== 'base') return;
        
        hasBaseSku = true;
        
        // Get unit price from rate card
        const unitPrice = getSkuPrice(pm.sales_sku, quantity);
        if (unitPrice === null) return; // SKU not in rate card
        
        // Calculate base cost
        const multiplier = pm.quantity_multiplier || 1;
        baseTotal += unitPrice * quantity * multiplier;
        
        // Add setup fee (one-time fee, not per unit, based on order quantity)
        const setupFee = getSkuSetupFee(pm.sales_sku, quantity);
        setupFeeTotal += setupFee;

        // Add per-unit add-ons attached to this base SKU (billed as
        // `addon_unit_price × qty` — mirrors the backend engine so the
        // live wizard summary matches the review page total).
        baseTotal += getSkuPerUnitAddons(pm.sales_sku, quantity);
      });
    });
    
    // Return null if no base SKU is configured, otherwise return the total (even if 0)
    return hasBaseSku ? baseTotal + setupFeeTotal : null;
  }, [flowConfig, getSkuPrice, getSkuSetupFee, getSkuPerUnitAddons]);

  // Check if base pricing is properly configured (for current step)
  const hasValidBasePrice = useMemo(() => {
    const quantity = specs.quantity || 500;
    const basePrice = calculateBasePrice(selections, quantity, currentStepIndex);
    return basePrice !== null && basePrice > 0;
  }, [selections, specs.quantity, calculateBasePrice, currentStepIndex]);

  // Computed base price from flow SKUs up to current step (null if not configured)
  const flowBasePrice = useMemo(() => {
    const quantity = specs.quantity || 500;
    return calculateBasePrice(selections, quantity, currentStepIndex);
  }, [selections, specs.quantity, calculateBasePrice, currentStepIndex]);

  // Computed total price including addons (only up to current step)
  // Returns null if no valid pricing is configured
  //
  // Precedence:
  //   1. Backend `flow-price-preview` result (SAME engine as review page,
  //      quote PDF and saved-order document — the ONLY correct source).
  //   2. Local fallback (flowBasePrice + addons) — used only while the
  //      backend request is in-flight or when the flow has no id yet.
  //
  // We deliberately DO NOT fall back to `priceEstimate.total_estimate`
  // (the legacy vinyl estimator) — that was the historical bug that
  // caused Blu-ray amortized orders to show a vinyl-flavoured extra
  // ~21k DKK on top of the correct amortized amount.
  const totalWithAddons = useMemo(() => {
    if (backendPrice && backendPrice.total !== null && backendPrice.total !== undefined) {
      return backendPrice.total;
    }
    const quantity = specs.quantity || 500;
    const addons = calculateAddonTotal(selections, quantity, currentStepIndex) || 0;
    if (flowBasePrice !== null && flowBasePrice > 0) {
      return flowBasePrice + addons;
    }
    if (flowBasePrice === null && addons > 0) {
      // Amortized-only / addon-only flow: no base SKU picked yet, but
      // we still have a valid subtotal from the addons/amortized lines.
      return addons;
    }
    return null;
  }, [backendPrice, flowBasePrice, specs.quantity, selections, calculateAddonTotal, currentStepIndex]);

  // Computed unit price with addons (null if total is null)
  const unitPriceWithAddons = useMemo(() => {
    if (totalWithAddons === null) return null;
    const quantity = specs.quantity || 500;
    if (quantity === 0) return null;
    return totalWithAddons / quantity;
  }, [totalWithAddons, specs.quantity]);

  // Display values in the customer's local currency. The rule is
  // ABSOLUTE: the numbers shown to the customer are the numbers the
  // backend engine produced (SAME engine as review page, quote PDF,
  // locked invoice basis and e-conomic sync). We never do a client-side
  // "unit = total_eur / qty" or "unit_local = convertFromEur(unit_eur)"
  // — that path historically caused source-currency (EUR) values to be
  // shown with the customer's local currency label (e.g. "0.10 kr" for
  // a 100 EUR / 1000 units amortized line when the correct answer is
  // "0.75 kr" from `(100 × 7.473) / 1000`).
  //
  // While the backend request is in flight we show a skeleton (see the
  // render sites) rather than a stale/local number.
  const displayUnitPriceLocal = useMemo(() => {
    if (backendPrice && backendPrice.unit_price_local != null) {
      return backendPrice.unit_price_local;
    }
    return null;
  }, [backendPrice]);

  const displayTotalLocal = useMemo(() => {
    if (backendPrice && backendPrice.total_local != null) {
      return backendPrice.total_local;
    }
    return null;
  }, [backendPrice]);

  // Computed addon total for display (only up to current step, null if no addons configured)
  const addonTotal = useMemo(() => {
    const quantity = specs.quantity || 500;
    const total = calculateAddonTotal(selections, quantity, currentStepIndex);
    return total;
  }, [selections, specs.quantity, calculateAddonTotal, currentStepIndex]);

  // Live price from the SHARED backend engine — debounced so rapid
  // clicks in the wizard don't hammer the API. Sends the exact fields
  // that would land on the order document at submission time so the
  // response is byte-identical to `GET /orders/{id}/price` ONCE the
  // customer reaches the final step.
  //
  // Progressive-reveal contract: while the customer is stepping through
  // the flow, we only include selections from steps they have already
  // REACHED (index ≤ currentStepIndex). Default options for steps that
  // haven't been reached yet stay marked in `selections` (so navigating
  // back doesn't lose them) but are stripped from the payload — which
  // means the displayed total only reflects the customer's committed
  // choices and each new step transparently adds its own contribution.
  // On the final step, all steps are reached so the returned total
  // equals the full saved-order price.
  useEffect(() => {
    if (!flowConfig?.id) {
      setBackendPrice(null);
      return;
    }
    const quantity = parseInt(specs.quantity) || 0;
    if (quantity <= 0) {
      setBackendPrice(null);
      return;
    }

    // Build a step-reached-scoped copy of selections.
    const visibleSelections = {};
    (flowConfig.steps || []).forEach((step, stepIndex) => {
      if (stepIndex <= currentStepIndex && selections[step.step_id]) {
        visibleSelections[step.step_id] = selections[step.step_id];
      }
    });

    const controller = new AbortController();
    const timer = setTimeout(async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/api/sales/flow-price-preview`, {
          method: 'POST',
          signal: controller.signal,
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            config_flow_id: flowConfig.id,
            config_selections: visibleSelections,
            quantity,
            // Prefer an explicitly-passed org (order-scoped editing);
            // fall back to the authenticated user's active company so
            // fresh Affinity Programme signups (KasKas / DK-Artisten)
            // hit the currency resolver's programme path and see
            // DKK immediately even before Billing Profile completion.
            organization_id: orderMetadata?.organization_id
              || authUser?.active_company_id
              || authUser?.organization_id
              || null,
            product_type: orderMetadata?.product_type || productFamily || null,
            order_type: orderMetadata?.is_reorder ? 'reorder' : 'new_order',
          }),
        });
        if (!res.ok) {
          setBackendPrice(null);
          return;
        }
        const data = await res.json();
        setBackendPrice(data);
      } catch (e) {
        if (e.name !== 'AbortError') {
          console.warn('[FlowDrivenBuilder] flow-price-preview failed:', e);
          setBackendPrice(null);
        }
      }
    }, 350);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [flowConfig?.id, flowConfig?.steps, selections, currentStepIndex, specs.quantity, orderMetadata?.organization_id, orderMetadata?.product_type, orderMetadata?.is_reorder, productFamily, authUser?.active_company_id, authUser?.organization_id]);

  // Fetch flow configuration
  useEffect(() => {
    // If parent already supplied a preloaded flow and we're not loading a
    // saved-by-ID flow, reuse it as-is and skip the network round-trip.
    if (initialFlowConfig && !savedFlowConfig?.flowId) {
      // Default selections still need initialising once on first mount.
      if (!initializedRef.current) {
        const defaultSelections = {};
        initialFlowConfig.steps?.forEach(step => {
          const selectedOptionIds = [];
          step.options?.forEach(o => { if (o.is_mandatory) selectedOptionIds.push(o.option_id); });
          const defaultOption = step.options?.find(o => o.is_default);
          if (defaultOption && !selectedOptionIds.includes(defaultOption.option_id)) {
            selectedOptionIds.push(defaultOption.option_id);
          }
          defaultSelections[step.step_id] = selectedOptionIds;
        });
        setSelections(defaultSelections);
        initializedRef.current = true;
      }
      // Fetch rate card prices async (non-blocking — UI is already usable)
      if (initialFlowConfig.default_rate_card_id) {
        (async () => {
          try {
            const token = localStorage.getItem('token');
            const r = await fetch(`${API_URL}/api/sales/rate-cards/${initialFlowConfig.default_rate_card_id}/public-prices`, {
              headers: token ? { Authorization: `Bearer ${token}` } : {},
            });
            if (r.ok) {
              const data = await r.json();
              const items = Array.isArray(data) ? data : (data.items || []);
              const priceMap = {};
              items.forEach(item => {
                const entry = {
                  display_name: item.display_name,
                  tiers: item.tiers || [],
                  category: item.category,
                  setup_fee_skus: item.setup_fee_skus || []
                };
                priceMap[item.sales_sku] = entry;
                const norm = normalizeSkuKey(item.sales_sku);
                if (norm && !priceMap[norm]) priceMap[norm] = entry;
              });
              setRateCardPrices(priceMap);
              if (!Array.isArray(data) && data.customer_discount) {
                setCustomerDiscount(data.customer_discount);
              }
            }
          } catch (e) { /* non-fatal */ }
        })();
      }
      setFlowLoading(false);
      return;
    }

    const fetchFlow = async () => {
      setFlowLoading(true);
      setFlowError(null);
      
      try {
        let flow;
        
        // If we have a saved flow ID, fetch that specific flow
        if (savedFlowConfig?.flowId) {
          console.log('[FlowDrivenBuilder] Loading saved flow:', savedFlowConfig.flowId);
          const res = await fetch(
            `${API_URL}/api/product-config/flows/${savedFlowConfig.flowId}`
          );
          
          if (!res.ok) {
            // Fall back to default flow if saved flow not found
            console.warn('[FlowDrivenBuilder] Saved flow not found, falling back to default');
            const defaultRes = await fetch(
              `${API_URL}/api/product-config/flows/by-mode/visual?product_family=${encodeURIComponent(productFamily)}`
            );
            if (!defaultRes.ok) throw new Error('Failed to load flow configuration');
            flow = await defaultRes.json();
          } else {
            flow = await res.json();
          }
        } else {
          // No saved flow, fetch default
          const res = await fetch(
            `${API_URL}/api/product-config/flows/by-mode/visual?product_family=${encodeURIComponent(productFamily)}`
          );
          
          if (!res.ok) {
            throw new Error('Failed to load flow configuration');
          }
          
          flow = await res.json();
        }
        
        if (!flow) {
          throw new Error('No active Visual Builder flow found');
        }
        
        setFlowConfig(flow);
        
        // Initialize selections - use saved selections if available, otherwise use defaults
        if (savedFlowConfig?.selections && Object.keys(savedFlowConfig.selections).length > 0 && !initializedRef.current) {
          console.log('[FlowDrivenBuilder] Restoring saved selections:', savedFlowConfig.selections);
          // Ensure mandatory options are included even in saved selections
          const selectionsWithMandatory = { ...savedFlowConfig.selections };
          flow.steps.forEach(step => {
            const mandatoryOptionIds = step.options?.filter(o => o.is_mandatory).map(o => o.option_id) || [];
            const currentSelections = selectionsWithMandatory[step.step_id] || [];
            // Add any mandatory options not already selected
            mandatoryOptionIds.forEach(optionId => {
              if (!currentSelections.includes(optionId)) {
                currentSelections.push(optionId);
              }
            });
            if (currentSelections.length > 0) {
              selectionsWithMandatory[step.step_id] = currentSelections;
            }
          });
          setSelections(selectionsWithMandatory);
          initializedRef.current = true;
        } else if (!initializedRef.current) {
          // Initialize with defaults and mandatory options
          console.log('[FlowDrivenBuilder] No saved selections, using defaults');
          const defaultSelections = {};
          flow.steps.forEach(step => {
            const selectedOptionIds = [];
            // Add mandatory options first
            step.options?.forEach(o => {
              if (o.is_mandatory) {
                selectedOptionIds.push(o.option_id);
              }
            });
            // Add default option if not already mandatory
            const defaultOption = step.options?.find(o => o.is_default);
            if (defaultOption && !selectedOptionIds.includes(defaultOption.option_id)) {
              selectedOptionIds.push(defaultOption.option_id);
            }
            defaultSelections[step.step_id] = selectedOptionIds;
          });
          setSelections(defaultSelections);
          initializedRef.current = true;
        }
        
        // Fetch rate card prices if flow has a rate card
        if (flow.default_rate_card_id) {
          try {
            // Attach the customer token so backend applies their discount.
            const token = localStorage.getItem('token');
            const rateCardRes = await fetch(
              `${API_URL}/api/sales/rate-cards/${flow.default_rate_card_id}/public-prices`,
              { headers: token ? { Authorization: `Bearer ${token}` } : {} }
            );
            if (rateCardRes.ok) {
              const data = await rateCardRes.json();
              const items = Array.isArray(data) ? data : (data.items || []);
              const priceMap = {};
              items.forEach(item => {
                const entry = {
                  display_name: item.display_name,
                  tiers: item.tiers || [],
                  category: item.category,
                  // Linked SKUs for setup fees and per-unit add-ons — the
                  // backend now inlines their tiers (even when they live
                  // in a different rate card) so the Visual Builder's live
                  // price summary can resolve the full cost without a
                  // second network round-trip.
                  setup_fee_skus: item.setup_fee_skus || [],
                  per_unit_addon_skus: item.per_unit_addon_skus || []
                };
                // Index under BOTH the raw sales_sku and its normalised form
                // (trimmed + underscores → spaces, lower-cased). Handles
                // user config typos like "BD_Master" referencing "BD Master"
                // — otherwise the linked setup fee silently drops to 0.
                priceMap[item.sales_sku] = entry;
                const norm = normalizeSkuKey(item.sales_sku);
                if (norm && !priceMap[norm]) priceMap[norm] = entry;
              });
              setRateCardPrices(priceMap);
              if (!Array.isArray(data) && data.customer_discount) {
                setCustomerDiscount(data.customer_discount);
              }
              console.log('Loaded pricing for', Object.keys(priceMap).length, 'SKUs');
            }
          } catch (e) {
            console.warn('Failed to fetch rate card prices:', e);
          }
        }
        
      } catch (err) {
        console.error('Flow fetch error:', err);
        setFlowError(err.message);
      } finally {
        setFlowLoading(false);
      }
    };
    
    fetchFlow();
  }, [savedFlowConfig?.flowId, savedFlowConfig?.selections, productFamily, initialFlowConfig]);

  // Fetch Visual Builder assets — re-fetch when language changes so
  // the customer sees translated labels/descriptions immediately.
  useEffect(() => {
    const fetchAssets = async () => {
      try {
        const langQ = language && language !== 'en' ? `&lang=${encodeURIComponent(language)}` : '';
        const res = await fetch(`${API_URL}/api/visual-builder/assets?active_only=true&product_family=${encodeURIComponent(productFamily)}${langQ}`);
        if (res.ok) {
          const data = await res.json();
          setAssets(data);
        }
      } catch (err) {
        console.error('Assets fetch error:', err);
      }
    };
    
    fetchAssets();
  }, [productFamily, language]);

  // Update specs when selections change
  useEffect(() => {
    if (!flowConfig) return;
    
    // Map selections to vinyl_specs format
    // This is a simplified mapping - adjust based on your specs structure
    const newSpecs = { ...specs };
    
    // Format selection
    if (selections.format?.length > 0) {
      newSpecs.format = selections.format[0];
    }
    
    // Record weight
    if (selections.record_weight?.length > 0) {
      const weight = selections.record_weight[0];
      newSpecs.vinyl_weight = weight === '180g' ? 180 : 140;
    }
    
    // Record color
    if (selections.record_color?.length > 0) {
      const color = selections.record_color[0];
      newSpecs.color_mode = color === 'black' ? 'black' : 'color';
      newSpecs.color_name = color;
    }
    
    // Inner sleeve
    if (selections.inner_sleeve?.length > 0) {
      newSpecs.inner_sleeve = selections.inner_sleeve[0];
    }
    
    // Cover
    if (selections.cover?.length > 0) {
      newSpecs.jacket_style = selections.cover[0];
    }
    
    // Cover finish
    if (selections.cover_finish?.length > 0) {
      newSpecs.cover_finish = selections.cover_finish[0];
    }
    
    // Extras (multi-select)
    if (selections.extras?.length > 0) {
      newSpecs.extras = selections.extras;
    }
    
    setSpecs(newSpecs);
  }, [selections, flowConfig]);

  // Recalculate price when specs change
  useEffect(() => {
    if (!specs.quantity) return;
    const timer = setTimeout(() => {
      getEstimate(specs);
    }, 500);
    return () => clearTimeout(timer);
  }, [specs, getEstimate]);

  // Current step
  const currentStep = flowConfig?.steps?.[currentStepIndex];
  const totalSteps = flowConfig?.steps?.length || 0;
  
  // Validation state for attribute errors
  const [attributeErrors, setAttributeErrors] = useState({});
  
  // Get required attribute fields for current step's selected options
  const getRequiredAttributeFields = useCallback(() => {
    if (!currentStep?.options) return [];
    
    const stepSelections = selections[currentStep.step_id] || [];
    const requiredFields = [];
    
    stepSelections.forEach(optionId => {
      const optionConfig = currentStep.options.find(o => o.option_id === optionId);
      if (!optionConfig?.additional_fields) return;
      
      optionConfig.additional_fields.forEach(field => {
        if (field.required) {
          requiredFields.push({
            stepId: currentStep.step_id,
            optionId,
            fieldId: field.field_id,
            label: field.label,
            isMulti: field.selection_mode === 'multi'
          });
        }
      });
    });
    
    return requiredFields;
  }, [currentStep, selections]);
  
  // Validate attribute fields for current step
  const validateAttributeFields = useCallback(() => {
    const requiredFields = getRequiredAttributeFields();
    const errors = {};
    let firstErrorFieldId = null;
    
    requiredFields.forEach(({ stepId, optionId, fieldId, label, isMulti }) => {
      const value = optionAttributes?.[stepId]?.[optionId]?.[fieldId];
      const isEmpty = isMulti 
        ? (!Array.isArray(value) || value.length === 0)
        : (!value || value === '');
      
      if (isEmpty) {
        const key = `${stepId}-${optionId}-${fieldId}`;
        errors[key] = `${label} is required`;
        if (!firstErrorFieldId) {
          firstErrorFieldId = `field-${fieldId}`;
        }
      }
    });
    
    setAttributeErrors(errors);
    
    // Scroll to first error
    if (firstErrorFieldId) {
      const element = document.querySelector(`[data-testid="${firstErrorFieldId}-trigger"]`) ||
                      document.querySelector(`[data-testid="${firstErrorFieldId}"]`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        element.focus?.();
      }
    }
    
    return Object.keys(errors).length === 0;
  }, [getRequiredAttributeFields, optionAttributes]);
  
  // Validation: check if current step has required selection
  const isCurrentStepValid = useMemo(() => {
    if (!currentStep) return true;
    if (!currentStep.is_required) return true;
    const stepSelections = selections[currentStep.step_id] || [];
    return stepSelections.length > 0;
  }, [currentStep, selections]);

  // Handle selection
  const handleSelect = useCallback((stepId, optionIds) => {
    setSelections(prev => ({
      ...prev,
      [stepId]: optionIds
    }));
  }, []);

  // Handle attribute change for option metadata fields
  const handleAttributeChange = useCallback((stepId, optionId, values) => {
    setOptionAttributes(prev => ({
      ...prev,
      [stepId]: {
        ...(prev[stepId] || {}),
        [optionId]: values
      }
    }));
  }, []);

  // Navigation
  const handleNext = () => {
    // Validate required attribute fields before proceeding
    if (!validateAttributeFields()) {
      return; // Don't proceed if validation fails
    }
    
    // Clear errors for this step
    setAttributeErrors({});
    
    if (currentStepIndex < totalSteps - 1) {
      setCurrentStepIndex(prev => prev + 1);
    } else {
      // Complete - include flow versioning data
      handleComplete();
    }
  };

  const handlePrev = () => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(prev => prev - 1);
    } else {
      onBack?.();
    }
  };

  // Build a snapshot of option attributes with labels for order storage
  // This ensures list changes don't affect old orders
  const buildAttributeSnapshot = useCallback(async () => {
    const snapshot = {};
    
    for (const stepId of Object.keys(optionAttributes)) {
      snapshot[stepId] = {};
      
      for (const optionId of Object.keys(optionAttributes[stepId])) {
        snapshot[stepId][optionId] = {};
        const optionData = optionAttributes[stepId][optionId];
        
        // Find the step and option config to get field definitions
        const step = flowConfig?.steps?.find(s => s.step_id === stepId);
        const optionConfig = step?.options?.find(o => o.option_id === optionId);
        
        if (!optionConfig?.additional_fields) continue;
        
        for (const field of optionConfig.additional_fields) {
          const fieldId = field.field_id;
          const value = optionData[fieldId];
          
          if (value === undefined || value === null || value === '') continue;
          
          // Store both value and label for list-backed fields
          if (field.source_list_id) {
            // Fetch the label(s) from the list
            try {
              const res = await fetch(`${API_URL}/api/product-config/attribute-lists/${field.source_list_id}/items`);
              if (res.ok) {
                const items = await res.json();
                const itemMap = Object.fromEntries(items.map(i => [i.value, i.label]));
                
                if (Array.isArray(value)) {
                  snapshot[stepId][optionId][fieldId] = {
                    values: value,
                    labels: value.map(v => itemMap[v] || v),
                    source_list: field.source_list_id,
                    field_label: field.label
                  };
                } else {
                  snapshot[stepId][optionId][fieldId] = {
                    value: value,
                    label: itemMap[value] || value,
                    source_list: field.source_list_id,
                    field_label: field.label
                  };
                }
              }
            } catch (err) {
              // Fallback to just storing value
              snapshot[stepId][optionId][fieldId] = {
                value: value,
                label: value,
                field_label: field.label
              };
            }
          } else {
            // Non-list field - just store value
            snapshot[stepId][optionId][fieldId] = {
              value: value,
              field_label: field.label
            };
          }
        }
      }
    }
    
    return snapshot;
  }, [optionAttributes, flowConfig]);

  const handleComplete = async () => {
    // Build attribute snapshot with labels
    const attributeSnapshot = await buildAttributeSnapshot();
    
    // Build final specs with flow versioning
    const finalSpecs = {
      ...specs,
      // Store flow versioning for order locking
      _flow_id: flowConfig.id,
      _flow_version: flowConfig.version,
      _flow_selections: selections,
      // Store option attributes with snapshotted labels (no pricing impact)
      _option_attributes: attributeSnapshot,
      // Pass through the quote-gate signal so the parent (LandingPage /
      // CampaignLandingPage) routes the submission to
      // ``POST /api/orders/request-quote`` instead of the normal
      // ``POST /api/orders`` → ``submit`` sequence. The server
      // revalidates the flag before accepting, so this is only a hint
      // for the frontend router — the authoritative check is backend.
      _requires_quote: !!(backendPrice && backendPrice.requires_quote),
      _quote_gated_skus: (backendPrice && backendPrice.quote_gated_skus) || null,
    };
    
    onComplete?.(finalSpecs, priceEstimate);
  };

  // Quantity change handler
  const handleQuantityChange = (qty) => {
    setSpecs(prev => ({ ...prev, quantity: qty }));
  };

  // ==================== MOBILE/TABLET SUMMARY BAR (below xl) ====================
  const MobileSummaryBar = () => {
    if (!flowConfig?.steps) return null;

    // Build format label for context
    const assetLabelMap = {};
    assets.forEach(a => { if (a.option_id && a.label) assetLabelMap[a.option_id] = a.label; });
    const formatStep = flowConfig.steps[0];
    const formatSel = selections[formatStep?.step_id]?.[0];
    const formatLbl = formatSel
      ? (formatStep?.options?.find(o => o.option_id === formatSel)?.label || assetLabelMap[formatSel] || formatSel)
      : null;

    // Selection summary for expanded view
    const getOptionLabel = (optId, stepOptions) => {
      const opt = stepOptions?.find(o => o.option_id === optId);
      if (opt?.label) return opt.label;
      if (assetLabelMap[optId]) return assetLabelMap[optId];
      return optId.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    };

    return (
      <div className="xl:hidden fixed bottom-0 left-0 right-0 z-40" data-testid="mobile-summary-bar">
        {/* Expandable detail panel */}
        {mobileSummaryExpanded && (
          <div className="bg-white border-t border-slate-200 shadow-[0_-4px_20px_rgba(0,0,0,0.08)] px-4 py-3 max-h-[50vh] overflow-y-auto">
            <div className="max-w-3xl mx-auto">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-bold text-slate-900">{t('flow.your_order', 'Your Order')}</h4>
                <button onClick={() => setMobileSummaryExpanded(false)} className="text-xs text-slate-400 hover:text-slate-600">
                  Close
                </button>
              </div>
              <div className="space-y-0.5">
                {flowConfig.steps.map((s, idx) => {
                  const selected = selections[s.step_id] || [];
                  const isDone = idx < currentStepIndex;
                  const isCurrent = idx === currentStepIndex;
                  const labels = selected.map(optId => getOptionLabel(optId, s.options));
                  const display = labels.length > 0 ? labels.join(', ') : null;
                  return (
                    <div key={s.step_id} className={`flex justify-between py-1.5 text-xs ${isCurrent ? 'text-indigo-600' : ''}`}>
                      <span className="text-slate-400">{s.label}</span>
                      <span className={isDone || isCurrent ? 'text-slate-800 font-medium' : 'text-slate-300'}>
                        {(isDone || isCurrent) && display ? display : '\u2014'}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Always-visible bottom bar */}
        <div className="bg-white/95 backdrop-blur-sm border-t border-slate-200 shadow-[0_-2px_12px_rgba(0,0,0,0.06)]">
          <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between gap-4">
            {/* Left: Price + context */}
            <button onClick={() => setMobileSummaryExpanded(!mobileSummaryExpanded)} className="text-left min-w-0 flex-1">
              <div className="flex items-baseline gap-2">
                {!showPrices ? (
                  <span className="text-sm font-semibold text-slate-700">{t('flow.your_order', 'Your Order')}</span>
                ) : displayTotalLocal !== null && displayTotalLocal > 0 ? (
                  <span className={`inline-flex flex-col items-start transition-opacity duration-150 ${(isCalculating || currencyLoading) ? 'opacity-60' : 'opacity-100'}`}>
                    <span className="text-xl font-bold text-slate-900 font-display leading-tight">
                      {formatCurrencyAmount(
                        displayUnitPriceLocal,
                        customerCurrency, { decimals: 2 }
                      )}
                      <span className="text-xs font-medium text-slate-500 ml-1">/ unit</span>
                    </span>
                    <span className="text-[11px] text-slate-400 leading-tight">
                      {formatCurrencyAmount(
                        displayTotalLocal,
                        customerCurrency, { decimals: 0 }
                      )} total
                    </span>
                  </span>
                ) : (isCalculating || currencyLoading) ? (
                  <span className="inline-block h-6 w-20 rounded bg-slate-200/70 animate-pulse" aria-hidden="true" />
                ) : (
                  <span className="text-sm text-slate-400">\u2014</span>
                )}
              </div>
              <p className="text-[11px] text-slate-400 mt-0.5 truncate">
                {specs.quantity || 500} pcs{formatLbl ? ` \u00B7 ${formatLbl}` : ''}
                {' \u00B7 '}
                <span className="text-indigo-500 font-medium">
                  {mobileSummaryExpanded ? 'Hide details' : 'View details'}
                </span>
              </p>
            </button>

            {/* Right: Nav buttons */}
            <div className="flex items-center gap-2 shrink-0">
              {currentStepIndex > 0 && (
                <button
                  onClick={handlePrev}
                  className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 py-2 px-3 rounded-lg hover:bg-slate-100"
                  data-testid="mobile-back-btn"
                >
                  <ChevronLeft className="h-3.5 w-3.5" /> Back
                </button>
              )}
              <button
                onClick={handleNext}
                disabled={!isCurrentStepValid}
                className={`flex items-center justify-center gap-1.5 text-sm font-semibold py-2.5 px-6 rounded-xl transition-all ${
                  isCurrentStepValid
                    ? (publicUI
                        ? 'owntune-cta btn-active'
                        : 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-md shadow-indigo-600/25')
                    : 'bg-slate-100 text-slate-400 cursor-not-allowed'
                }`}
                data-testid="mobile-continue-btn"
              >
                {currentStepIndex === totalSteps - 1 ? 'Complete' : 'Continue'}
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ==================== FLOW ORDER SUMMARY PANEL (COMPACT FLOATING) ====================
  const FlowOrderSummary = () => {
    if (!flowConfig?.steps) return null;
    const allSteps = flowConfig.steps;

    // Build asset label lookup: option_id → label
    const assetLabelMap = {};
    assets.forEach(a => { if (a.option_id && a.label) assetLabelMap[a.option_id] = a.label; });

    const getOptionLabel = (optId, stepOptions) => {
      const opt = stepOptions?.find(o => o.option_id === optId);
      if (opt?.label) return opt.label;
      if (assetLabelMap[optId]) return assetLabelMap[optId];
      return optId.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    };

    // Derive format from first step selection for header context
    const formatStep = allSteps[0];
    const formatSelection = selections[formatStep?.step_id]?.[0];
    const formatLabel = formatSelection ? getOptionLabel(formatSelection, formatStep?.options) : null;

    return (
      <div className="sticky top-24 w-[360px] shrink-0" data-testid="flow-order-summary">
        <div className="rounded-2xl border border-slate-200 bg-white shadow-lg shadow-slate-200/60">
          {/* Header */}
          <div className="px-5 py-4 border-b border-slate-100 bg-gradient-to-b from-muted to-card">
            <h3 className="text-sm font-bold text-card-foreground">{t('flow.your_order', 'Your Order')}</h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              {specs.quantity || 500} pcs{formatLabel ? ` \u00B7 ${formatLabel}` : ''}
            </p>
          </div>

          {/* Selection lines */}
          <div className="py-1.5">
            {allSteps.map((s, idx) => {
              const selected = selections[s.step_id] || [];
              const isCurrent = idx === currentStepIndex;
              const isDone = idx < currentStepIndex;
              const selectedLabels = selected.map(optId => getOptionLabel(optId, s.options));
              const displayValue = selectedLabels.length > 0 ? selectedLabels.join(', ') : null;

              return (
                <div
                  key={s.step_id}
                  className={`flex items-center justify-between px-5 py-[7px] text-xs group cursor-default transition-colors ${
                    isCurrent ? 'bg-indigo-50/60' : 'hover:bg-slate-50/60'
                  }`}
                >
                  <span className={`truncate ${isCurrent ? 'text-indigo-500 font-medium' : 'text-slate-400'}`}>{s.label}</span>
                  <div className="flex items-center gap-1.5 ml-3 shrink-0">
                    <span className={`truncate max-w-[160px] text-right ${
                      isCurrent && displayValue ? 'text-indigo-700 font-semibold'
                        : isDone && displayValue ? 'text-slate-800 font-medium'
                        : 'text-slate-300'
                    }`}>
                      {(isDone || isCurrent) && displayValue ? displayValue : '\u2014'}
                    </span>
                    {isDone && (
                      <button
                        onClick={() => setCurrentStepIndex(idx)}
                        className="text-[10px] text-indigo-400 opacity-0 group-hover:opacity-100 transition-opacity font-semibold ml-0.5"
                        data-testid={`edit-flow-step-${idx}`}
                      >
                        Edit
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Price section — hidden entirely when showPrices is OFF */}
          {showPrices && <div className="mx-5 border-t border-slate-200" />}
          {showPrices && (
          <div className="px-5 py-4">
            {displayTotalLocal !== null && displayTotalLocal > 0 ? (
              <div className={`transition-opacity duration-150 ${(isCalculating || currencyLoading) ? 'opacity-60' : 'opacity-100'}`}>
                <div className="flex justify-end items-center gap-2">
                  {customerDiscount && (
                    <span
                      className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5"
                      data-testid="customer-discount-badge"
                      title={
                        customerDiscount.type === 'global'
                          ? `Your account has a ${customerDiscount.percent}% discount applied to all prices.`
                          : 'Your account has custom pricing applied.'
                      }
                    >
                      {customerDiscount.percent != null
                        ? `Your price · –${customerDiscount.percent}%`
                        : 'Your price'}
                    </span>
                  )}
                  <span className="text-xs text-slate-500">{t('flow.estimated_price', 'Estimated price')}</span>
                </div>
                <p className="text-2xl font-bold text-slate-900 font-display mt-1 text-right">
                  {formatCurrencyAmount(
                    displayUnitPriceLocal,
                    customerCurrency, { decimals: 2 }
                  )}
                  <span className="text-sm font-medium text-slate-500 ml-1">/ unit</span>
                </p>
                <p className="text-[11px] text-slate-400 mt-0.5 text-right">
                  {formatCurrencyAmount(
                    displayTotalLocal,
                    customerCurrency, { decimals: 0 }
                  )} total
                </p>
              </div>
            ) : (isCalculating || currencyLoading) ? (
              // First-time render before any estimate has arrived: subtle skeleton
              // (no "Calculating..." text) so the layout doesn't shift.
              <div className="flex flex-col items-end gap-1.5">
                <span className="text-xs text-slate-500">{t('flow.estimated_price', 'Estimated price')}</span>
                <span className="block h-7 w-24 rounded bg-slate-200/70 animate-pulse" aria-hidden="true" />
                <span className="block h-3 w-16 rounded bg-slate-200/70 animate-pulse" aria-hidden="true" />
              </div>
            ) : (
              <span className="text-xs text-slate-400">\u2014</span>
            )}
          </div>
          )}

          {/* Estimated timeline — anonymous marketing flow shows the
              full detail block only on the final review step; earlier
              steps get a single compact line under the price to keep
              the side panel focused on selection + price + CTA. Admin
              flow keeps the original full block on every step. */}
          <div className="mx-5 border-t border-slate-100" />
          {(!publicUI || currentStepIndex >= flowConfig.steps.length - 1) ? (
            <div className="px-5 py-3">
              <p className="text-[10px] uppercase tracking-wider text-slate-400 font-medium mb-2">{t('flow.estimated_timeline', 'Estimated timeline')}</p>
              <div className="space-y-1.5">
                {isVinylFamily && (
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-slate-500">{t('quick_order.test_press', 'Test press')}</span>
                    <span className="text-slate-700 font-medium">~4 weeks</span>
                  </div>
                )}
                <div className="flex items-center justify-between text-xs">
                  <span className="text-slate-500">{t('flow.full_production', 'Full production')}</span>
                  <span className="text-slate-700 font-medium">{isVinylFamily ? '~8 weeks' : '~2 weeks'}</span>
                </div>
              </div>
              <p className="text-[10px] text-slate-400 italic mt-1.5">{t('flow.timeline_disclaimer', 'Estimates only. Exact dates confirmed after order submission.')}</p>
            </div>
          ) : (
            <div className="px-5 py-2.5">
              <p className="text-[11px] text-slate-500 text-right">
                Estimated production: <span className="text-slate-700 font-medium">~8 weeks</span>
              </p>
            </div>
          )}

          {/* Navigation CTA */}
          <div className="px-5 pb-5 pt-1 flex items-center gap-2">
            {currentStepIndex > 0 && (
              <button
                onClick={handlePrev}
                className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 transition-colors py-2 px-3 rounded-lg hover:bg-slate-100"
                data-testid="summary-back-btn"
              >
                <ChevronLeft className="h-3.5 w-3.5" /> Back
              </button>
            )}
            <button
              onClick={handleNext}
              disabled={!isCurrentStepValid}
              className={`flex-1 flex items-center justify-center gap-1.5 text-sm font-semibold py-2.5 px-5 rounded-xl transition-all ${
                isCurrentStepValid
                  ? (publicUI
                      ? 'owntune-cta btn-active'
                      : 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-md shadow-indigo-600/25')
                  : 'bg-slate-100 text-slate-400 cursor-not-allowed'
              }`}
              data-testid="summary-continue-btn"
            >
              {currentStepIndex === totalSteps - 1 ? 'Complete' : 'Continue'}
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Loading state — only ever shown if the prefetch in ProductConfigWizard
  // didn't complete before the user clicked Guided Setup AND the parent
  // couldn't preload (e.g. a deep-link with `savedFlowConfig.flowId`).
  // Renders a small inline spinner in the page's normal flow — no
  // full-screen takeover.
  if (flowLoading) {
    return (
      <div className="bg-white rounded-xl border border-border overflow-hidden">
        <div className="h-1 bg-slate-100">
          <div className="h-full bg-indigo-500/40 animate-pulse" style={{ width: '14%' }} />
        </div>
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
        </div>
      </div>
    );
  }

  // Error state
  if (flowError) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center max-w-md">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-slate-900 mb-2">{t('flow.config_error', 'Configuration Error')}</h2>
          <p className="text-slate-600 mb-4">{flowError}</p>
          <Button onClick={onBack} variant="outline">
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  // No flow configured
  if (!flowConfig || !flowConfig.steps?.length) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center max-w-md">
          <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-slate-900 mb-2">{t('flow.no_config', 'No Configuration Available')}</h2>
          <p className="text-slate-600 mb-4">
            The Guided Setup flow has not been configured yet. Please contact an administrator.
          </p>
          <Button onClick={onBack} variant="outline">
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  const StepIcon = STEP_ICONS[currentStep?.asset_category] || STEP_ICONS.default;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <CompactHeader
        currentStep={currentStepIndex}
        steps={flowConfig.steps}
        onBack={handlePrev}
        orderMetadata={orderMetadata}
      />

      {/* Main Content — extra bottom padding below xl for mobile summary bar.
          Anonymous marketing flows (campaign / landing pages) use a
          wider max-w to match the original Visual Builder feel; admin
          keeps the tighter 6xl. */}
      <div className={`${publicUI ? 'max-w-[1440px]' : 'max-w-6xl'} mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-24 xl:pb-8`}>
        <div className="flex gap-8">
          <div className="flex-1 min-w-0">
            {/* Current Step */}
            <div className="space-y-6">
          {/* Step header — hidden in V2 text-hero variant because
              FlowStep renders it inside the LEFT column so the hero
              image aligns with the top of the "Your Order" card. */}
          {recordStepVariant !== 'text-hero' && (
          <div className="flex items-center gap-4">
            <div className="p-3 bg-indigo-100 rounded-xl">
              <StepIcon className="w-6 h-6 text-indigo-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-slate-900">{currentStep.label}</h2>
              {currentStep.description && (
                <p className="text-slate-500">{currentStep.description}</p>
              )}
            </div>
          </div>
          )}

          {/* Step content */}
          <FlowStep
            step={currentStep}
            assets={assets}
            selections={selections}
            onSelect={handleSelect}
            isActive={true}
            quantity={specs.quantity || 500}
            getOptionPriceInfo={getOptionPriceInfo}
            optionAttributes={optionAttributes}
            onAttributeChange={handleAttributeChange}
            attributeErrors={attributeErrors}
            customerCurrency={customerCurrency}
            rates={rates}
            recordStepVariant={recordStepVariant}
            showPrices={showPrices}
          />

          {/* Validation message */}
          {currentStep.is_required && !isCurrentStepValid && (
            <p className="text-sm text-amber-600 flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              Please make a selection to continue
            </p>
          )}
        </div>

        {/* Step 0: Show quantity selector below format selection */}
        {currentStepIndex === 0 && (
          <div className="mt-8 p-6 bg-card rounded-xl border border-border">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">{t('order.quantity_label', 'Quantity')}</h3>
            <QuantitySelector
              quantity={specs.quantity || 500}
              setQuantity={handleQuantityChange}
              format={specs.format}
              presets={qtyPresetsConfig.presets}
              minQty={qtyPresetsConfig.min_qty}
              maxQty={qtyPresetsConfig.max_qty}
              customEnabled={qtyPresetsConfig.custom_enabled}
            />
          </div>
        )}
          </div>
          {/* Floating sidebar summary — desktop only */}
          <div className="hidden xl:block">
            <FlowOrderSummary />
          </div>
        </div>
      </div>

      {/* Mobile/Tablet: Pinned bottom summary bar — visible below xl */}
      <MobileSummaryBar />
    </div>
  );
}
